import { ServerIcon } from "lucide-react";
import type { ProviderDriverKind } from "@heydex/contracts";
import { ProjectFavicon, type ProjectFaviconProject } from "./ProjectFavicon";
import { cn } from "~/lib/utils";

export const COMMAND_PALETTE_META_ICON_CLASS = "size-3 shrink-0 text-muted-foreground/70";

export function CommandPaletteMetaDot() {
  return <span className="shrink-0 text-muted-foreground/50">·</span>;
}

export function ThreadCommandSubtitle(props: {
  project: ProjectFaviconProject | null;
  projectTitle: string | null;
  environmentLabel?: string | null;
  isCurrent: boolean;
  driverKind?: ProviderDriverKind | null;
  providerDisplayName?: string | null;
  acpRegistryAgentId?: string | undefined;
  acpRegistryIconUrl?: string | undefined;
  className?: string;
}) {
  const showHarness = props.driverKind != null && props.providerDisplayName;
  const projectLabel = props.projectTitle?.trim() || null;

  if (!projectLabel && !props.isCurrent && !showHarness) {
    return null;
  }

  return (
    <span
      className={cn(
        "inline-flex min-w-0 max-w-full items-center gap-1 text-xs text-muted-foreground/70",
        props.className,
      )}
    >
      {projectLabel ? (
        <span className="inline-flex min-w-0 items-center gap-1">
          {props.project ? (
            <ProjectFavicon project={props.project} className="size-3 shrink-0" />
          ) : null}
          <span className="min-w-0 truncate">{projectLabel}</span>
          {props.environmentLabel ? (
            <>
              <CommandPaletteMetaDot />
              <span className="shrink-0">{props.environmentLabel}</span>
            </>
          ) : null}
        </span>
      ) : null}

      {showHarness && projectLabel ? <CommandPaletteMetaDot /> : null}

      {props.isCurrent ? (
        <>
          {projectLabel || showHarness ? <CommandPaletteMetaDot /> : null}
          <span className="shrink-0">Current thread</span>
        </>
      ) : null}
    </span>
  );
}

/** Where a folder lives, or the machines a grouped folder spans. */
export function ProjectSearchDescription(props: {
  readonly environmentLabels: ReadonlyArray<string>;
  readonly grouped: boolean;
  readonly location: {
    readonly kind: "local" | "remote";
    readonly label: string;
  };
  readonly workspaceRoot: string;
}) {
  if (!props.grouped) {
    return (
      <span className="flex min-w-0 items-center gap-1">
        <span className="inline-flex min-w-0 items-center gap-1">
          {props.location.kind === "remote" ? (
            <ServerIcon aria-hidden className={COMMAND_PALETTE_META_ICON_CLASS} />
          ) : null}
          <span className="truncate">{props.location.label}</span>
        </span>
        <CommandPaletteMetaDot />
        <span className="truncate">{props.workspaceRoot}</span>
      </span>
    );
  }

  return <span className="truncate">{props.environmentLabels.join(" · ")}</span>;
}
